import { Option } from "../../Autocomplete";
import { Button } from "../../Button";
import { z } from "zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import _ from "lodash";
import { Autocomplete, Dialog, DialogActions, DialogContent, DialogTitleWithCloseButton, TextField } from "../../../../shared/components";

interface CreateDialogProps {
    entityName: string;
    allEntities: Option[];
    availableEntities: Option[];
    acceptCallback: (args: Object) => void;
    closeCallback: () => void;
    enableLinkedAndWipEntities?: boolean;
}

const typeOptions = [
    { value: "Value", label: "Value" },
    { value: "Linked", label: "Linked" },
    { value: "Work in Progress", label: "Work in Progress" }
];

function CreateDialog(props: CreateDialogProps) {
    const { entityName, allEntities, availableEntities, acceptCallback, closeCallback, enableLinkedAndWipEntities } = props;

    const schema = z.object({
        name: z.string()
            .trim()
            .min(1, { error: `Please supply a name for the new ${entityName}.` }),
        type: z.object({
            value: z.string(),
            label: z.string()
        }, {
            error: `Please select a type for the new ${entityName}.`
        }),
        entity: z.object({
            value: z.string(),
            label: z.string()
        }).nullable().optional()
    }).superRefine((val, ctx) => {
        if ((val.type.value === "Linked" || val.type.value === "Work in Progress") && val.entity == null) {
            ctx.addIssue({
                code: "custom",
                message: `Please select a ${entityName} to use as the source.`,
                path: ["entity"]
            });
        }
    });
    type FormValues = z.infer<typeof schema>;

    const { handleSubmit, setError, control, watch } = useForm({ resolver: zodResolver(schema), defaultValues: { name: "", type: typeOptions[0], entity: null } });

    const typeValue = watch("type")?.value;
    const showEntityField = typeValue === "Linked" || typeValue === "Work in Progress";

    const onSubmit: SubmitHandler<FormValues> = formModel => {
        if (_.find(allEntities, e => e.label === formModel.name)) {
            setError("name", { message: `'${formModel.name}' is an existing ${entityName} Name!  Please choose a different name.`, type: "validation_error" });
            return;
        }

        acceptCallback({
            name: formModel.name,
            type: formModel.type?.value,
            entity_id: formModel.entity?.value,
            entity_label: formModel.entity?.label
        });
    };

    return (
        <Dialog
            open={true}
            onClose={(_event, reason) => {
                if (reason !== 'backdropClick') {
                    closeCallback();
                }
            }}
            disablePortal={true}
            aria-labelledby="form-dialog-title">
            <DialogTitleWithCloseButton onClose={closeCallback} id="form-dialog-title">
                Create New {entityName}
            </DialogTitleWithCloseButton>
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <DialogContent>
                    <div className="vertical-form-field">
                        Please supply a name for the new {entityName}:
                    </div>
                    <Controller
                        name="name"
                        control={control}
                        render={({
                            field,
                            fieldState: { error }
                        }) => (
                            <TextField
                                {...field}
                                error={!!error}
                                helperText={error?.message}
                                variant="outlined"
                                fullWidth
                            />
                        )}
                    />
                    {
                        enableLinkedAndWipEntities &&
                        <>
                            <div className="vertical-form-field">
                                {entityName} type:
                            </div>
                            <Controller
                                control={control}
                                name="type"
                                render={({
                                    field: { value, onChange },
                                    fieldState: { error }
                                }) => (
                                    <Autocomplete
                                        value={value}
                                        onChange={(_event, option, _reason) => onChange((option as Option))}
                                        error={!!error}
                                        helperText={error?.message}
                                        isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
                                        getOptionLabel={(option: any) => option?.label || ""}
                                        size="small"
                                        fullWidth
                                        options={typeOptions}
                                        disableClearable
                                    />
                                )}
                            />

                            {
                                showEntityField &&
                                <>
                                    <div className="vertical-form-field">
                                        Source {entityName}:
                                    </div>
                                    <Controller
                                        control={control}
                                        name="entity"
                                        render={({
                                            field: { value, onChange },
                                            fieldState: { error }
                                        }) => (
                                            <Autocomplete
                                                value={value}
                                                onChange={(_event, option, _reason) => onChange((option as Option))}
                                                error={!!error}
                                                helperText={error?.message}
                                                isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
                                                getOptionLabel={(option: any) => option?.label || ""}
                                                size="small"
                                                fullWidth
                                                options={availableEntities}
                                                disableClearable
                                            />
                                        )}
                                    />
                                </>
                            }
                        </>
                    }
                </DialogContent>
                <DialogActions>
                    <Button type="submit" variant="text" color="warning">
                        Create
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export { CreateDialog };